import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CollaborationStore from '@/store/collaboration.store';
import summaryCommonStore from '@/store/summaryCommon.store';
import { setTimeoutPromise } from '@/utils';
import { saveRecord } from '@/services/api_collaboration';
import CollaborationHandler from '@/bussiness/CollaborationHandler';
import { debounce } from '@common/utils';
import { wsUrl } from '@/pages/Collaboration/constant';
import useNetworkDetect from '@/utils/useNetworkDetect';
import useLoginStore from '@/store/login.store';
import { VISITOR_TOKEN_STORAGE_KEY } from '@/constants/user';
import { MessageType } from 'antd/es/message/interface';
import { message } from 'antd';
import { TOKEN_KEY } from '@/config';

const SILENT_GAP = 1000 * 60 * 1;

export const useCollaborationSocket = (streamNameProp?: string) => {
  const setStreamId = summaryCommonStore((state) => state.setStreamId);
  const meetingFinish = CollaborationStore((state) => state.meetingFinish);
  const setMeetingFinish = CollaborationStore((state) => state.setMeetingFinish);
  const init = CollaborationStore((state) => state.init);
  const unstableText = CollaborationStore((state) => state.unstableText);
  const setShowbar = CollaborationStore((state) => state.setShowSidebar);

  const [isNetworkInited, setIsNetworkInited] = useState(false);
  const [modalCancel, setModalCancel] = useState(false);
  const [showJumpSummaryDialog, setShowJumpSummaryDialog] = useState(false);
  const [newStreamId, setNewStreamId] = useState('');
  const { isVisitor } = useLoginStore();
  const networkErrorToastCancelRef = useRef<MessageType | null>(null);

  const socketTimer = useRef<number | null>(null);
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  let streamId = searchParams.get('streamId');
  if (streamNameProp) {
    streamId = streamNameProp;
  }
  else if (!streamId?.includes("live/")) {
    streamId = `live/${streamId}`;
  }


  const pollingSaveRecord = async (maxRetry = 20) => {
    let _retryTimes = maxRetry;
    while (_retryTimes > 0) {
      try {
        const res = await saveRecord({
          streamName: streamId,
        });
        if (!res.targetStreamName) {
          throw new Error('没保存好，继续轮询');
        }
        return res;
      } catch (error) {
        console.log('pollingSaveShare  error:', error);
        await setTimeoutPromise(5000);
        _retryTimes--;
      }
    }
    return null;
  };

  const initSocket = debounce(() => {
    //会中加入需要鉴权
    const token = isVisitor ? localStorage.getItem(VISITOR_TOKEN_STORAGE_KEY) : localStorage.getItem(TOKEN_KEY);
    const authorization = isVisitor ? `${token}` : `Bearer ${token}`;
    const url = wsUrl + '?authorization=' + authorization;
    CollaborationHandler.initSocket(streamId, url);
  }, 200);

  const [isOnline, startDetect, stopDetect] = useNetworkDetect({
    onPingEnd: async (pingSuccess: boolean) => {
      if (!isVisitor) {
        const res = await pollingSaveRecord(1);
        if (res) {
          setNewStreamId(res.targetStreamName);
          setMeetingFinish(true);
          setShowJumpSummaryDialog(true);
        }
      }

      pingSuccess && stopDetect();
    },
  });

  const handleSaveRecord = async () => {
    const res = await pollingSaveRecord();
    if (!res?.targetStreamName) {
      throw new Error('保存异常');
    }
    const newStreamId = res.targetStreamName as string;
    setNewStreamId(newStreamId);
  };

  const handleFinish = async () => {
    try {
      if (!isVisitor) {
        await handleSaveRecord();
      }
      !modalCancel && setShowJumpSummaryDialog(true);
    } catch (e) {
      console.error('保存异常');
    }
  };

  const handleJumpSummary = async () => {
    try {
      setShowJumpSummaryDialog(false);
      navigate(`/summary?streamId=${newStreamId.replace('live/', '')}`, { replace: true });
    } catch (error) {
      console.error('保存异常');
    }
  };

  // 网络状态变化监听
  useEffect(() => {
    if (!isNetworkInited) {
      setIsNetworkInited(true);
      return;
    }
    if (isOnline) {
      console.log('网络恢复');
      networkErrorToastCancelRef.current?.()
      stopDetect();
      initSocket();
    } else {
      console.log('网络异常');
      networkErrorToastCancelRef.current = message.error('网络异常，请检查网络设置', 0);
    }
    return () => {
      networkErrorToastCancelRef.current?.()
    };
  }, [isOnline]);

  // 静默检测定时器
  useEffect(() => {
    socketTimer.current = window.setInterval(async () => {
      console.log('一分钟没吐字');
      stopDetect();
      startDetect();
    }, SILENT_GAP);
    return () => {
      socketTimer.current && window.clearInterval(socketTimer.current);
    };
  }, [unstableText]);

  // 初始化和清理
  useEffect(() => {
    init();
    CollaborationStore.getState().resetRecentSpeakers();
    CollaborationStore.getState().clearRoleSpeakerMapping();
    streamId && setStreamId(streamId);
    if (streamId) {
      initSocket();
      setShowbar(true);
    }
  }, [streamId]);

  useEffect(() => {
    return () => {
      init();
      CollaborationHandler.stopSocket();
      stopDetect();
      socketTimer.current && window.clearInterval(socketTimer.current);
    };
  }, []);

  // 会议结束处理
  useEffect(() => {
    console.log('meetingFinish = ', meetingFinish);
    if (meetingFinish) {
      handleFinish();
    }
  }, [meetingFinish]);

  return {
    streamId,
    setMeetingFinish,
    showJumpSummaryDialog,
    setShowJumpSummaryDialog,
    setModalCancel,
    handleJumpSummary,
    navigate,
  };
}; 